import httpClient from "@utils/httpClient";

// Type Definitions
export interface AssessmentStats {
  totalUsers: number;
  totalAssessments: number;
  totalResults: number;
}

export interface AssessmentResult {
  resultId: number;
  userId: number;
  name: string;
  email: string;
  score: number;
  totalQuestions: number | null;
  percentage: number | null;
  submittedAt: string;
  assessmentTitle: string;
  isCarveAssessment: boolean;
  emailSent: boolean;
}

export interface AssessmentResultsData {
  assessmentId: number;
  totalResults: number;
  results: AssessmentResult[];
}

export interface Assessment {
  assessmentId: number;
  assessmentTitle: string;
  description?: string;
  assessment_type_id?: number;
  hasAttempted?: boolean;
  course?: string;
  creator?: string;
  goal?: string;
  startTime?: string;
  endTime?: string;
}

export interface AssessmentType {
  assessment_type_id: number;
  assessment_type: string;
}

export interface CreateAssessmentData {
  assessmentTitle: string;
  course?: string;
  creator?: string;
  goal?: string;
  suggestCareer?: boolean;
  startTime?: string;
  endTime?: string;
  assessment_type_id: number;
}

interface ApiResponse<T = any> {
  success: boolean;
  data: T | null;
  error: { message: string } | null;
}

/**
 * Get admin statistics from assessment API
 * @returns {Promise<ApiResponse<AssessmentStats>>} Assessment statistics
 */
export const getAdminStats = async (): Promise<ApiResponse<AssessmentStats>> => {
  try {
    const response = await httpClient.get('/assessments/admin/stats');

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('getAdminStats error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch admin stats' }
    };
  }
};

/**
 * Get all assessments
 * @returns {Promise<ApiResponse<Assessment[]>>} List of assessments
 */
export const getAssessments = async (): Promise<ApiResponse<Assessment[]>> => {
  try {
    const response = await httpClient.get('/assessments');

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('getAssessments error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch assessments' }
    };
  }
};

/**
 * Get a specific assessment by ID
 * @param {number} assessmentId - Assessment ID
 * @returns {Promise<ApiResponse<Assessment>>} Assessment details
 */
export const getAssessmentById = async (assessmentId: number): Promise<ApiResponse<Assessment>> => {
  try {
    const response = await httpClient.get(`/assessments/${assessmentId}`);

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('getAssessmentById error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch assessment' }
    };
  }
};

/**
 * Get all draft assessments
 * @returns {Promise<ApiResponse<Assessment[]>>} List of draft assessments
 */
export const getDraftAssessments = async (): Promise<ApiResponse<Assessment[]>> => {
  try {
    const response = await httpClient.get('/assessments/drafts');

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('getDraftAssessments error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch draft assessments' }
    };
  }
};

/**
 * Get all review assessments
 * @returns {Promise<ApiResponse<Assessment[]>>} List of review assessments
 */
export const getReviewAssessments = async (): Promise<ApiResponse<Assessment[]>> => {
  try {
    const response = await httpClient.get('/assessments/review');

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('getReviewAssessments error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch review assessments' }
    };
  }
};

/**
 * Get results for a specific assessment
 * @param {number} assessmentId - Assessment ID
 * @returns {Promise<ApiResponse<AssessmentResultsData>>} Assessment results
 */
export const getAssessmentResults = async (assessmentId: number): Promise<ApiResponse<AssessmentResultsData>> => {
  try {
    const response = await httpClient.get(`/assessments/${assessmentId}/results`);

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('getAssessmentResults error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch assessment results' }
    };
  }
};

/**
 * Download assessment report for a specific user
 * @param {number} assessmentId - Assessment ID
 * @param {number} userId - User ID
 * @returns {Promise<ApiResponse<Blob>>} PDF blob
 */
export const downloadAssessmentReport = async (assessmentId: number, userId: number): Promise<ApiResponse<Blob>> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/assessments/${assessmentId}/download-report?userId=${userId}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to download report');
    }

    const blob = await response.blob();

    return {
      success: true,
      data: blob,
      error: null
    };
  } catch (error: any) {
    console.error('downloadAssessmentReport error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to download assessment report' }
    };
  }
};

/**
 * Send assessment report via email to a specific user
 * @param {number} assessmentId - Assessment ID
 * @param {number} userId - User ID
 * @returns {Promise<ApiResponse<any>>} Email response
 */
export const emailAssessmentReport = async (assessmentId: number, userId: number): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post(`/assessments/${assessmentId}/email-report`, { userId });

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('emailAssessmentReport error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to email assessment report' }
    };
  }
};

/**
 * Download all assessment results as Excel
 * @param {number} assessmentId - Assessment ID
 * @returns {Promise<ApiResponse<Blob>>} Excel blob
 */
export const downloadAssessmentExcel = async (assessmentId: number): Promise<ApiResponse<Blob>> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/assessments/${assessmentId}/download-excel`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to download Excel');
    }

    const blob = await response.blob();

    return {
      success: true,
      data: blob,
      error: null
    };
  } catch (error: any) {
    console.error('downloadAssessmentExcel error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to download assessment Excel' }
    };
  }
};

/**
 * Download selected users' assessment results as Excel
 * @param {number} assessmentId - Assessment ID
 * @param {number[]} userIds - Array of user IDs
 * @returns {Promise<ApiResponse<Blob>>} Excel blob
 */
export const downloadSelectedExcel = async (assessmentId: number, userIds: number[]): Promise<ApiResponse<Blob>> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/assessments/${assessmentId}/download-excel-selected`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ userIds })
      }
    );

    if (!response.ok) {
      throw new Error('Failed to download selected Excel');
    }

    const blob = await response.blob();

    return {
      success: true,
      data: blob,
      error: null
    };
  } catch (error: any) {
    console.error('downloadSelectedExcel error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to download selected assessment Excel' }
    };
  }
};

/**
 * Get all assessment types
 * @returns {Promise<ApiResponse<AssessmentType[]>>} List of assessment types
 */
export const getAssessmentTypes = async (): Promise<ApiResponse<AssessmentType[]>> => {
  try {
    const response = await httpClient.get('/assessments/types');

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('getAssessmentTypes error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch assessment types' }
    };
  }
};

/**
 * Create a new assessment
 * @param {CreateAssessmentData} assessmentData - Assessment data to create
 * @returns {Promise<ApiResponse<any>>} Created assessment response
 */
export const createAssessment = async (assessmentData: CreateAssessmentData): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post('/assessments/create', assessmentData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('createAssessment error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to create assessment' }
    };
  }
};

// Module Assessment Mapping Types
export interface Dimension {
  dimension_id: number;
  dimension_name: string;
  chart_id: number | null;
  chart_type?: string;
}

export interface Module {
  moduleId: number;
  title: string;
}

export interface ChartType {
  chart_id: number;
  chart_name: string;
}

export interface ModuleAssessmentMapping {
  module_id: number;
  assessment_id: number;
  dimension_id: number;
  module_name?: string;
  dimension_name?: string;
}

export interface CreateDimensionData {
  dimension_name: string;
  chart_id?: number;
}

export interface CreateModuleData {
  title: string;
}

export interface CreateMappingData {
  module_id: number;
  assessment_id: number;
  dimension_id: number;
}

/**
 * Get all dimensions with chart information
 * @returns {Promise<ApiResponse<Dimension[]>>} List of dimensions
 */
export const getDimensions = async (): Promise<ApiResponse<Dimension[]>> => {
  try {
    const response = await httpClient.get('/assessments/mapping/dimensions');

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('getDimensions error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch dimensions' }
    };
  }
};

/**
 * Get all modules
 * @returns {Promise<ApiResponse<Module[]>>} List of modules
 */
export const getModules = async (): Promise<ApiResponse<Module[]>> => {
  try {
    const response = await httpClient.get('/assessments/mapping/modules');

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('getModules error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch modules' }
    };
  }
};

/**
 * Get all chart types
 * @returns {Promise<ApiResponse<ChartType[]>>} List of chart types
 */
export const getChartTypes = async (): Promise<ApiResponse<ChartType[]>> => {
  try {
    const response = await httpClient.get('/assessments/mapping/chart-types');

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('getChartTypes error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch chart types' }
    };
  }
};

/**
 * Create a new dimension
 * @param {CreateDimensionData} dimensionData - Dimension data to create
 * @returns {Promise<ApiResponse<any>>} Created dimension response
 */
export const createDimension = async (dimensionData: CreateDimensionData): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post('/assessments/mapping/dimensions', dimensionData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('createDimension error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to create dimension' }
    };
  }
};

/**
 * Create a new module
 * @param {CreateModuleData} moduleData - Module data to create
 * @returns {Promise<ApiResponse<any>>} Created module response
 */
export const createModule = async (moduleData: CreateModuleData): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post('/assessments/mapping/modules', moduleData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('createModule error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to create module' }
    };
  }
};

/**
 * Update a dimension
 * @param {number} dimension_id - Dimension ID
 * @param {CreateDimensionData} dimensionData - Updated dimension data
 * @returns {Promise<ApiResponse<any>>} Updated dimension response
 */
export const updateDimension = async (dimension_id: number, dimensionData: CreateDimensionData): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.put(`/assessments/mapping/dimensions/${dimension_id}`, dimensionData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('updateDimension error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to update dimension' }
    };
  }
};

/**
 * Delete a dimension
 * @param {number} dimension_id - Dimension ID
 * @returns {Promise<ApiResponse<any>>} Delete response
 */
export const deleteDimension = async (dimension_id: number): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.delete(`/assessments/mapping/dimensions/${dimension_id}`);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('deleteDimension error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to delete dimension' }
    };
  }
};

/**
 * Update a module
 * @param {number} moduleId - Module ID
 * @param {CreateModuleData} moduleData - Updated module data
 * @returns {Promise<ApiResponse<any>>} Updated module response
 */
export const updateModule = async (moduleId: number, moduleData: CreateModuleData): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.put(`/assessments/mapping/modules/${moduleId}`, moduleData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('updateModule error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to update module' }
    };
  }
};

/**
 * Delete a module
 * @param {number} moduleId - Module ID
 * @returns {Promise<ApiResponse<any>>} Delete response
 */
export const deleteModule = async (moduleId: number): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.delete(`/assessments/mapping/modules/${moduleId}`);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('deleteModule error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to delete module' }
    };
  }
};

/**
 * Get module-assessment mappings for a specific assessment
 * @param {number} assessmentId - Assessment ID
 * @returns {Promise<ApiResponse<ModuleAssessmentMapping[]>>} List of mappings
 */
export const getModuleAssessmentMappings = async (assessmentId: number): Promise<ApiResponse<ModuleAssessmentMapping[]>> => {
  try {
    const response = await httpClient.get(`/assessments/${assessmentId}/module-mappings`);

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('getModuleAssessmentMappings error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch module mappings' }
    };
  }
};

/**
 * Create a new module-assessment mapping
 * @param {CreateMappingData} mappingData - Mapping data to create
 * @returns {Promise<ApiResponse<any>>} Created mapping response
 */
export const createModuleAssessmentMapping = async (mappingData: CreateMappingData): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post('/assessments/module-mappings', mappingData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('createModuleAssessmentMapping error:', error);
    // Error message is already extracted by nucleus-api and httpClient
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to create module mapping' }
    };
  }
};

/**
 * Delete a module-assessment mapping
 * @param {number} module_id - Module ID
 * @param {number} assessment_id - Assessment ID
 * @param {number} dimension_id - Dimension ID
 * @returns {Promise<ApiResponse<any>>} Delete response
 */
export const deleteModuleAssessmentMapping = async (
  module_id: number,
  assessment_id: number,
  dimension_id: number
): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post('/assessments/module-mappings/delete', {
      module_id,
      assessment_id,
      dimension_id
    });

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('deleteModuleAssessmentMapping error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to delete module mapping' }
    };
  }
};

// Question Management Types
export interface QuestionOption {
  text: string;
  isCorrect: boolean;
  value?: number; // For 360 feedback Likert scale
}

export interface CreateQuestionData {
  moduleId: number;
  text: string;
  options?: QuestionOption[]; // omitted/empty for subjective (free-text) feedback questions
  applicable_to?: 'participant' | 'reportee' | 'manager'; // For 360/270 feedback
  question_type?: 'objective' | 'subjective'; // 270 feedback: subjective = free-text, no options
}

export interface Question {
  questionId: number;
  moduleId: number;
  text: string;
  type: string;
  imageUrl: string | null;
  createdAt: string;
  categoryId: number | null;
  options?: QuestionOption[];
}

/**
 * Get modules for a specific assessment
 * @param {number} assessmentId - Assessment ID
 * @returns {Promise<ApiResponse<Module[]>>} List of modules
 */
export const getAssessmentModules = async (assessmentId: number): Promise<ApiResponse<Module[]>> => {
  try {
    const response = await httpClient.get(`/assessments/${assessmentId}/modules`);

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('getAssessmentModules error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch assessment modules' }
    };
  }
};

/**
 * Create a new question with options
 * @param {CreateQuestionData} questionData - Question data to create
 * @returns {Promise<ApiResponse<any>>} Created question response
 */
export const createQuestion = async (questionData: CreateQuestionData): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post('/assessments/questions', questionData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('createQuestion error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to create question' }
    };
  }
};

/**
 * Get questions for a specific module (includes options)
 * @param {number} moduleId - Module ID
 * @returns {Promise<ApiResponse<Question[]>>} List of questions with options
 */
export const getModuleQuestions = async (moduleId: number): Promise<ApiResponse<Question[]>> => {
  try {
    const response = await httpClient.get(`/assessments/modules/${moduleId}/questions`);

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('getModuleQuestions error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch module questions' }
    };
  }
};

// User Management Types
export interface User {
  userId: number;
  name: string;
  email: string;
  dob: string;
  rollId: number;
  role: string; // 'Admin' or 'User'
  createdAt: string;
  is_leader?: number; // 0 or 1
}

export interface AssessmentUser {
  id: number;
  name: string;
  email: string;
  is_leader: number; // 0 or 1
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalUsers: number;
  usersPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface UsersResponse {
  users: User[];
  pagination: PaginationInfo;
}

export interface UpdateUserData {
  name: string;
  email: string;
  dob: string;
}

/**
 * Get all users with pagination
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Number of users per page (default: 50)
 * @returns {Promise<ApiResponse<UsersResponse>>} List of users with pagination info
 */
export const getAllUsers = async (page: number = 1, limit: number = 50): Promise<ApiResponse<UsersResponse>> => {
  try {
    const response = await httpClient.get('/assessments/admin/users', {
      params: { page, limit }
    });

    // Backend returns { status, data: users[], pagination: {...} }
    return {
      success: true,
      data: {
        users: response.data || [],
        pagination: response.pagination || {
          currentPage: page,
          totalPages: 1,
          totalUsers: 0,
          usersPerPage: limit,
          hasNextPage: false,
          hasPreviousPage: false
        }
      },
      error: null
    };
  } catch (error: any) {
    console.error('getAllUsers error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch users' }
    };
  }
};

/**
 * Update user details
 * @param {number} userId - User ID
 * @param {UpdateUserData} userData - Updated user data
 * @returns {Promise<ApiResponse<any>>} Update response
 */
export const updateUser = async (userId: number, userData: UpdateUserData): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.put(`/assessments/admin/users/${userId}`, userData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('updateUser error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to update user' }
    };
  }
};

/**
 * Delete a user
 * @param {number} userId - User ID
 * @returns {Promise<ApiResponse<any>>} Delete response
 */
export const deleteUser = async (userId: number): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.delete(`/assessments/admin/users/${userId}`);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('deleteUser error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to delete user' }
    };
  }
};

// Assessment Update Types
export interface UpdatePublishedAssessmentData {
  startTime?: string;
  endTime?: string;
}

export interface UpdateDraftAssessmentData {
  assessmentTitle?: string;
  course?: string;
  creator?: string;
  goal?: string;
  suggestCareer?: boolean;
  startTime?: string;
  endTime?: string;
  assessment_type_id?: number;
  status?: string; // 'draft' | 'review' | 'ready'
}

/**
 * Update published assessment (only startTime and endTime)
 * @param {number} assessmentId - Assessment ID
 * @param {UpdatePublishedAssessmentData} updateData - Data to update
 * @returns {Promise<ApiResponse<any>>} Update response
 */
export const updatePublishedAssessment = async (
  assessmentId: number,
  updateData: UpdatePublishedAssessmentData
): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.put(
      `/assessments/${assessmentId}/update-published`,
      updateData
    );

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('updatePublishedAssessment error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to update published assessment' }
    };
  }
};

/**
 * Update draft assessment (all fields including status)
 * @param {number} assessmentId - Assessment ID
 * @param {UpdateDraftAssessmentData} updateData - Data to update
 * @returns {Promise<ApiResponse<any>>} Update response
 */
export const updateDraftAssessment = async (
  assessmentId: number,
  updateData: UpdateDraftAssessmentData
): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.put(
      `/assessments/${assessmentId}/update-draft`,
      updateData
    );

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('updateDraftAssessment error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to update draft assessment' }
    };
  }
};

/**
 * Delete assessment (soft delete)
 * @param {number} assessmentId - Assessment ID
 * @returns {Promise<ApiResponse<any>>} Delete response
 */
export const deleteAssessment = async (
  assessmentId: number
): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.delete(
      `/assessments/${assessmentId}/delete`
    );

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('deleteAssessment error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to delete assessment' }
    };
  }
};

// 360 Feedback Question Management Types
export interface Feedback360Question {
  question_id: number;
  moduleId: number;
  module_title?: string;
  question_text: string;
  question_order: number;
  respondent_type: 'self' | 'manager' | 'reportee';
  is_active: boolean;
  created_at: string;
}

export interface CreateFeedback360QuestionData {
  assessmentId: number;
  moduleId: number;
  questionText: string;
  respondentType: 'self' | 'manager' | 'reportee';
}

/**
 * Create a new 360 feedback question
 * @param {CreateFeedback360QuestionData} questionData - Question data to create
 * @returns {Promise<ApiResponse<any>>} Created question response
 */
export const create360FeedbackQuestion = async (
  questionData: CreateFeedback360QuestionData
): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post('/360-feedback/questions', questionData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('create360FeedbackQuestion error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to create 360 feedback question' }
    };
  }
};

/**
 * Get 360 feedback questions by assessment
 * @param {number} assessmentId - Assessment ID
 * @param {number} moduleId - Module ID (optional)
 * @param {string} respondentType - Respondent type (optional)
 * @returns {Promise<ApiResponse<Feedback360Question[]>>} List of questions
 */
export const get360FeedbackQuestions = async (
  assessmentId: number,
  moduleId?: number,
  respondentType?: 'self' | 'manager' | 'reportee'
): Promise<ApiResponse<Feedback360Question[]>> => {
  try {
    const params: any = {};
    if (moduleId) params.moduleId = moduleId;
    if (respondentType) params.respondentType = respondentType;

    const response = await httpClient.get(`/360-feedback/questions/${assessmentId}`, { params });

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('get360FeedbackQuestions error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch 360 feedback questions' }
    };
  }
};

// ============================================================================
// 360 FEEDBACK ASSESSMENT TAKING TYPES & METHODS
// ============================================================================

export interface Feedback360Module {
  module_id: number;
  module_name: string;
  questions: Feedback360QuestionItem[];
}

export interface Feedback360QuestionItem {
  question_id: number;
  question_text: string;
  question_order: number;
}

export interface Feedback360AssessmentConfig {
  config_id: number;
  assessment_id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  title: string;
  status: 'pending' | 'self_completed' | 'waiting_responses' | 'completed';
  self_submitted: boolean;
  self_submitted_at?: string;
  created_at: string;
  expires_at?: string;
}

export interface Feedback360Response {
  question_id: number;
  response_value: number; // 1-5 rating
}

export interface Feedback360SubmissionData {
  user_id: number;
  responses: Feedback360Response[];
}

export interface Feedback360AssessmentStatus {
  config_id: number;
  status: string;
  self_submitted: boolean;
  self_submitted_at?: string;
  manager_responses: {
    total: number;
    completed: number;
    pending: number;
    tokens: Array<{
      respondent_name: string;
      respondent_email: string;
      submitted: boolean;
      submitted_at?: string;
    }>;
  };
  reportee_responses: {
    total: number;
    completed: number;
    pending: number;
    tokens: Array<{
      respondent_name: string;
      respondent_email: string;
      submitted: boolean;
      submitted_at?: string;
    }>;
  };
  completion: {
    can_calculate: boolean;
    reasons?: string[];
  };
  results_available: boolean;
}

export interface Feedback360Results {
  config_id: number;
  user_id: number;
  user_name: string;
  assessment_title: string;
  module_results: Array<{
    module_id: number;
    module_name: string;
    self_score?: number;
    manager_avg_score?: number;
    reportee_avg_score?: number;
    overall_avg_score: number;
  }>;
  overall_summary: {
    self_avg?: number;
    manager_avg?: number;
    reportee_avg?: number;
    overall_avg: number;
  };
}

export interface TokenValidation {
  valid: boolean;
  reason?: string;
  config_id?: number;
  respondent_user_id?: number;
  respondent_type?: 'manager' | 'reportee';
  assessment_title?: string;
  employee_name?: string;
}

/**
 * Get self-assessment questions (for authenticated users taking their own assessment)
 * @returns {Promise<ApiResponse<Feedback360Module[]>>} Questions grouped by module
 */
export const getSelf360Questions = async (): Promise<ApiResponse<Feedback360Module[]>> => {
  try {
    const response = await httpClient.get('/360-feedback/questions/self');

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('getSelf360Questions error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch self-assessment questions' }
    };
  }
};

/**
 * Get all 360 assessments for a specific user
 * @param {number} userId - User ID
 * @returns {Promise<ApiResponse<Feedback360AssessmentConfig[]>>} List of assessments
 */
export const getUser360Assessments = async (userId: number): Promise<ApiResponse<Feedback360AssessmentConfig[]>> => {
  try {
    const response = await httpClient.get(`/360-feedback/user/${userId}`);

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('getUser360Assessments error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch user assessments' }
    };
  }
};

/**
 * Get specific 360 assessment details
 * @param {number} configId - Config ID
 * @returns {Promise<ApiResponse<Feedback360AssessmentConfig>>} Assessment details
 */
export const get360AssessmentDetails = async (configId: number): Promise<ApiResponse<Feedback360AssessmentConfig>> => {
  try {
    const response = await httpClient.get(`/360-feedback/assessment/${configId}`);

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('get360AssessmentDetails error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch assessment details' }
    };
  }
};

/**
 * Submit self-assessment responses
 * @param {number} configId - Config ID
 * @param {Feedback360SubmissionData} submissionData - Submission data
 * @returns {Promise<ApiResponse<any>>} Submission result
 */
export const submitSelf360Assessment = async (
  configId: number,
  submissionData: Feedback360SubmissionData
): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post(`/360-feedback/${configId}/submit-self`, submissionData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('submitSelf360Assessment error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to submit self-assessment' }
    };
  }
};

/**
 * Get 360 assessment status (completion tracking)
 * @param {number} configId - Config ID
 * @returns {Promise<ApiResponse<Feedback360AssessmentStatus>>} Status details
 */
export const get360AssessmentStatus = async (configId: number): Promise<ApiResponse<Feedback360AssessmentStatus>> => {
  try {
    const response = await httpClient.get(`/360-feedback/${configId}/status`);

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('get360AssessmentStatus error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch assessment status' }
    };
  }
};

/**
 * Get 360 assessment results
 * @param {number} configId - Config ID
 * @returns {Promise<ApiResponse<Feedback360Results>>} Results
 */
export const get360AssessmentResults = async (configId: number): Promise<ApiResponse<Feedback360Results>> => {
  try {
    const response = await httpClient.get(`/360-feedback/${configId}/results`);

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('get360AssessmentResults error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch results' }
    };
  }
};

// ============================================================================
// TOKEN-BASED 360 FEEDBACK METHODS (for managers/reportees)
// ============================================================================

/**
 * Validate 360 feedback token
 * @param {string} token - Access token
 * @returns {Promise<ApiResponse<TokenValidation>>} Validation result
 */
export const validate360Token = async (token: string): Promise<ApiResponse<TokenValidation>> => {
  try {
    const response = await httpClient.get(`/360-feedback/token/${token}/validate`);

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('validate360Token error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to validate token' }
    };
  }
};

/**
 * Get questions for token-based assessment
 * @param {string} token - Access token
 * @returns {Promise<ApiResponse<Feedback360Module[]>>} Questions grouped by module
 */
export const getToken360Questions = async (token: string): Promise<ApiResponse<Feedback360Module[]>> => {
  try {
    const response = await httpClient.get(`/360-feedback/token/${token}/questions`);

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('getToken360Questions error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch questions' }
    };
  }
};

/**
 * Submit external respondent assessment (manager/reportee)
 * @param {string} token - Access token
 * @param {Feedback360Response[]} responses - Responses
 * @returns {Promise<ApiResponse<any>>} Submission result
 */
export const submitExternal360Assessment = async (
  token: string,
  responses: Feedback360Response[]
): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post(`/360-feedback/token/${token}/submit`, { responses });

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('submitExternal360Assessment error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to submit assessment' }
    };
  }
};

// ============================================================================
// USER LEADER FLAG MANAGEMENT
// ============================================================================

/**
 * Get all assessment users with leader flag
 * @returns {Promise<ApiResponse<AssessmentUser[]>>} List of assessment users
 */
export const getAssessmentUsers = async (): Promise<ApiResponse<AssessmentUser[]>> => {
  try {
    const response = await httpClient.get('/assessments/users/assessment-users');

    return {
      success: true,
      data: response || [],
      error: null
    };
  } catch (error: any) {
    console.error('getAssessmentUsers error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch assessment users' }
    };
  }
};

/**
 * Update user leader flag
 * @param {number} userId - User ID
 * @param {number} isLeader - Leader flag (0 or 1)
 * @returns {Promise<ApiResponse<any>>} Update response
 */
export const updateUserLeaderFlag = async (userId: number, isLeader: number): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.put(
      `/assessments/users/assessment-users/${userId}/leader`,
      { is_leader: isLeader }
    );

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('updateUserLeaderFlag error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to update leader flag' }
    };
  }
};
